import React, { useState, useContext, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReactECharts from 'echarts-for-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
        faPencilAlt, faArrowsAltH, faArrowLeft, faLocationDot, faClock,
        faBatteryFull, faBatteryHalf, faBatteryQuarter, faWater, faTrash,
        faTemperatureHigh, faDroplet, faLightbulb, faSeedling, faExclamationTriangle, faCheck
} from '@fortawesome/free-solid-svg-icons';
import { formatRelativeDate } from '../utils/date';
import { AuthContext } from '../context/AuthContext';
import { ROLE_SUPERADMIN } from '../utils/roles';
import { updateDevice, deleteDevice, uploadDeviceImage, deleteDeviceImage } from '../utils/api';
import MoveDeviceModal from '../components/MoveDeviceModal';
import RenameDeviceModal from '../components/RenameDeviceModal';
import ImageUploadModal from '../components/ImageUploadModal';
import ConfirmationModal from '../components/ConfirmationModal';
import Tooltip from '../components/Tooltip';
import HistoryRangeSelector from '../components/HistoryRangeSelector';
import { useCustomerData } from '../hooks/useCustomerData';
import { useDeviceData } from '../hooks/useDeviceData';
import { useChartData } from '../hooks/useChartData';
import { useMoistureForecast } from '../hooks/useMoistureForecast';
import { useModalState } from '../hooks/useModalState';
import { useAlertAcknowledgements } from '../hooks/useAlertAcknowledgements';
import { DEFAULT_HISTORY_RANGE } from '../constants/historyRanges';
import { useToast } from '../context/ToastContext';
import Lottie from 'lottie-react';
import happyPlantAnimation from '../animations/happy-plant.json';

// dryingSoonLabel words the moisture warning. The forecast only reaches the UI
// within its 3-day horizon, so these are the only cases.
const dryingSoonLabel = (days) => {
        if (days <= 1) return 'Likely dry tomorrow';
        return `Likely dry in ${days} days`;
};

// overwaterTimes words how many times a plant was overwatered, as the
// Overwatered alert's message does.
const overwaterTimes = (count) => (count === 1 ? 'once' : count === 2 ? 'twice' : `${count} times`);

// The overwatering notice is left once an Overwatered alert has cleared but the
// soil has not yet dried below the too-wet line: a short badge titled like the
// alert, the count and advice in its tooltip.
const overwaterNoticeLabel = 'Too wet';
const overwaterNoticeText = (count) =>
        `You have overwatered this plant ${overwaterTimes(count)}. You should give it more time to dry out before watering.`;

// How long an acknowledged alert's chip draws attention after it arrives.
const ACK_HIGHLIGHT_MS = 2400;

const DeviceDetailsPage = () => {
        const { user } = useContext(AuthContext);
        const { deviceId } = useParams();
        const navigate = useNavigate();

        // Data hooks
        const { customer } = useCustomerData(user?.customerId);
        const { device, sensorData, loading, error, refresh: refreshDevice } = useDeviceData(deviceId);
        // One range drives all four trend charts, so they always cover the same
        // window and can be read against each other.
        const [trendsRange, setTrendsRange] = useState(DEFAULT_HISTORY_RANGE);
        const { chartData, firstReadingAt } = useChartData(deviceId, 'raw', trendsRange);
        const { forecast: moistureForecast } = useMoistureForecast(deviceId);
        const { acknowledgedAt, isAcknowledged, acknowledge } = useAlertAcknowledgements(deviceId);
        // The alert just acknowledged: its chip under Trends & Analytics is
        // scrolled to and lit up, to show where the alert went.
        const [arrivedAlertId, setArrivedAlertId] = useState(null);

        // Modal states
        const moveModal = useModalState();
        const renameModal = useModalState();
        const uploadModal = useModalState();
        const deleteImageModal = useModalState();
        const deleteDeviceModal = useModalState();
        const { showToast } = useToast();
        const [imageVersion, setImageVersion] = useState(0);

        // Memoized location lookup
        const { siteName, roomName } = useMemo(() => {
                if (!customer?.sites || !device?.room_id) {
                        return { siteName: 'N/A', roomName: 'N/A' };
                }
                for (const site of customer.sites) {
                        const room = site.rooms?.find(r => r.id === device.room_id);
                        if (room) {
                                return { siteName: site.name, roomName: room.name };
                        }
                }
                return { siteName: 'N/A', roomName: 'N/A' };
        }, [customer, device]);

        // Memoized derived values
        const deviceInfo = useMemo(() => {
                if (!device || !sensorData) return null;

                const getBatteryIcon = (level) => {
                        if (level > 19) return { icon: faBatteryFull, className: 'text-status-ok' };
                        if (level > 9) return { icon: faBatteryHalf, className: 'text-status-warn' };
                        return { icon: faBatteryQuarter, className: 'text-status-critical' };
                };

                const dateTime = sensorData.updated_at ? formatRelativeDate(sensorData.updated_at) : 'Never';
                const installedDate = device.created_at ? formatRelativeDate(device.created_at) : 'N/A';
                const battery = sensorData.battery_level !== undefined ? sensorData.battery_level : '--';
                const batteryInfo = battery !== '--' ? getBatteryIcon(battery) : null;
                const imgSrc = device.image ? `/api/v2/devices/${device.id}/image?v=${imageVersion}` : 'https://storage.googleapis.com/uxpilot-auth.appspot.com/e4c5def903-e02e5cf35c228c7398d8.png';

                return { dateTime, installedDate, battery, batteryInfo, imgSrc };
        }, [device, sensorData, imageVersion]);

        // Memoized ECharts configurations
        const chartOptions = useMemo(() => {
                const getLightLabel = (lux) => {
                        if (lux < 50) return 'Full Shade';
                        if (lux < 150) return 'Partly Shade';
                        if (lux < 2200) return 'Bright Light';
                        return 'Direct Light';
                };

                const normalizeLux = (lux) => {
                        if (lux <= 0) return 0;
                        if (lux < 50) return lux / 50;
                        if (lux < 150) return 1 + (lux - 50) / 100;
                        if (lux < 2200) return 2 + (lux - 150) / 2050;
                        return 3;
                };

                const lightData = Array.isArray(chartData.light_values) && chartData.light_values.length > 0
                        ? chartData.light_values.map(d => ({ value: [d[0], normalizeLux(d[1])], originalLux: d[1] }))
                        : [];

                const getNutritionLabel = (val) => {
                        if (val < 600) return 'Too Low';
                        if (val < 1200) return 'Good';
                        return 'Too High';
                };

                const normalizeNutrition = (val) => {
                        if (val <= 0) return 0;
                        if (val < 600) return val / 600;
                        if (val < 1200) return 1 + (val - 600) / 600;
                        return 2;
                };

                const nutritionData = Array.isArray(chartData.nutrition_values) && chartData.nutrition_values.length > 0
                        ? chartData.nutrition_values.map(d => ({ value: [d[0], normalizeNutrition(d[1])], originalVal: d[1] }))
                        : [];

                const getAreaGradient = (color) => ({
                        color: {
                                type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                                colorStops: [
                                        { offset: 0, color: color.replace('1)', '0.3)') },
                                        { offset: 1, color: color.replace('1)', '0.02)') }
                                ]
                        }
                });

                const colors = {
                        temp: 'rgba(220, 38, 38, 1)',
                        moist: 'rgba(59, 130, 246, 1)',
                        light: 'rgba(245, 158, 11, 1)',
                        nutrition: 'rgba(16, 185, 129, 1)'
                };

                return {
                        temp: {
                                grid: { top: 40, bottom: 40, left: 50, right: 20 },
                                xAxis: { type: 'time', splitLine: { show: true, lineStyle: { type: 'dashed' } } },
                                yAxis: {
                                        type: 'value',
                                        min: (value) => Math.floor(value.min - 5),
                                        max: (value) => Math.ceil(value.max + 5),
                                        name: 'Temperature (°C)',
                                        nameRotate: 90,
                                        nameLocation: 'middle',
                                        nameTextStyle: { fontWeight: 600, fontSize: 13, fontFamily: 'Lexend', align: 'center' },
                                        axisLabel: {}
                                },
                                tooltip: { trigger: 'axis' },
                                series: [{
                                        name: 'Temperature', type: 'line', data: chartData.temp_values,
                                        smooth: true, showSymbol: false,
                                        itemStyle: { color: colors.temp },
                                        areaStyle: getAreaGradient(colors.temp)
                                }],
                                dataZoom: [
                                        {
                                                type: 'inside',
                                                xAxisIndex: 0,
                                                start: 0,
                                                end: 100
                                        },
                                        {
                                                type: 'slider',
                                                xAxisIndex: 0,
                                                start: 0,
                                                end: 100,
                                                bottom: 10
                                        }
                                ],
                        },
                        moisture: {
                                grid: { top: 40, bottom: 40, left: 50, right: 20 },
                                xAxis: { type: 'time', splitLine: { show: true, lineStyle: { type: 'dashed' } } },
                                yAxis: {
                                        type: 'value', min: 0, max: 100,
                                        name: 'Moisture (%)',
                                        nameRotate: 90,
                                        nameLocation: 'middle',
                                        nameTextStyle: { fontWeight: 600, fontSize: 13, fontFamily: 'Lexend', align: 'center' },
                                        axisLabel: {}
                                },
                                tooltip: { trigger: 'axis' },
                                series: [{
                                        name: 'Moisture', type: 'line', data: chartData.moist_values,
                                        smooth: true, showSymbol: false,
                                        itemStyle: { color: colors.moist },
                                        areaStyle: getAreaGradient(colors.moist)
                                }],
                                dataZoom: [
                                        {
                                                type: 'inside',
                                                xAxisIndex: 0,
                                                start: 0,
                                                end: 100
                                        },
                                        {
                                                type: 'slider',
                                                xAxisIndex: 0,
                                                start: 0,
                                                end: 100,
                                                bottom: 10
                                        }
                                ],
                        },
                        light: {
                                grid: { top: 40, bottom: 40, left: 100, right: 20 },
                                xAxis: { type: 'time', splitLine: { show: true, lineStyle: { type: 'dashed' } } },
                                yAxis: {
                                        type: 'value', min: 0, max: 3, interval: 1,
                                        nameTextStyle: { fontWeight: 'bold', align: 'right' },
                                        axisLabel: {
                                                formatter: (val) => {
                                                        const levels = { 0: 'Full Shade', 1: 'Partly Shade', 2: 'Bright Light', 3: 'Direct Light' };
                                                        return levels[val] ?? '';
                                                }
                                        }
                                },
                                tooltip: {
                                        trigger: 'axis',
                                        formatter: (params) => {
                                                const p = params[0];
                                                const lux = p.data.originalLux;
                                                return `${p.axisValueLabel}<br/>${p.marker}${p.seriesName}: ${lux} (${getLightLabel(lux)})`;
                                        }
                                },
                                series: [{
                                        name: 'Level', type: 'line', data: lightData,
                                        smooth: true, showSymbol: false,
                                        itemStyle: { color: colors.light },
                                        areaStyle: getAreaGradient(colors.light),
                                }],
                                dataZoom: [
                                        {
                                                type: 'inside',
                                                xAxisIndex: 0,
                                                start: 0,
                                                end: 100
                                        },
                                        {
                                                type: 'slider',
                                                xAxisIndex: 0,
                                                start: 0,
                                                end: 100,
                                                bottom: 10
                                        }
                                ],
                        },
                        nutrition: {
                                grid: { top: 40, bottom: 40, left: 100, right: 20 },
                                xAxis: { type: 'time', splitLine: { show: true, lineStyle: { type: 'dashed' } } },
                                yAxis: {
                                        type: 'value', min: 0, max: 2, interval: 1,
                                        nameTextStyle: { fontWeight: 'bold', align: 'right' },
                                        axisLabel: {
                                                formatter: (val) => {
                                                        const levels = { 0: 'Too Low', 1: 'Good', 2: 'Too High' };
                                                        return levels[val] ?? '';
                                                }
                                        }
                                },
                                tooltip: {
                                        trigger: 'axis',
                                        formatter: (params) => {
                                                const p = params[0];
                                                const val = p.data.originalVal;
                                                return `${p.axisValueLabel}<br/>${p.marker}${p.seriesName}: ${val} (${getNutritionLabel(val)})`;
                                        }
                                },
                                series: [{
                                        name: 'Nutrition', type: 'line', data: nutritionData,
                                        smooth: true, showSymbol: false,
                                        itemStyle: { color: colors.nutrition },
                                        areaStyle: getAreaGradient(colors.nutrition)
                                }],
                                dataZoom: [
                                        {
                                                type: 'inside',
                                                xAxisIndex: 0,
                                                start: 0,
                                                end: 100
                                        },
                                        {
                                                type: 'slider',
                                                xAxisIndex: 0,
                                                start: 0,
                                                end: 100,
                                                bottom: 10
                                        }
                                ],
                        }
                };
        }, [chartData]);

        const getSensorIcon = useCallback((sensorType) => {
                switch (sensorType) {
                        case 'temp': return { icon: faTemperatureHigh, color: 'text-red-500' };
                        case 'moist': return { icon: faDroplet, color: 'text-blue-500' };
                        case 'light': return { icon: faLightbulb, color: 'text-yellow-500' };
                        case 'nutrition': return { icon: faSeedling, color: 'text-green-500' };
                        default: return { icon: faExclamationTriangle, color: 'text-orange-500' };
                }
        }, []);

        const openAlerts = (device?.alerts || []).filter(a => !isAcknowledged(a));
        const acknowledgedAlerts = (device?.alerts || []).filter(isAcknowledged);

        const handleAcknowledge = (alert) => {
                acknowledge(alert);
                setArrivedAlertId(alert.id);
        };

        useEffect(() => {
                if (arrivedAlertId == null) return;
                document.querySelector(`[data-testid="acknowledged-alert"][data-alert-id="${arrivedAlertId}"]`)
                        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                const timer = setTimeout(() => setArrivedAlertId(null), ACK_HIGHLIGHT_MS);
                return () => clearTimeout(timer);
        }, [arrivedAlertId]);

        // acknowledgedChips are the small yellow notes beside a chart's heading,
        // one per acknowledged alert of that sensor.
        const acknowledgedChips = (sensorType) => acknowledgedAlerts
                .filter(a => a.sensor_type === sensorType)
                .map(alert => (
                        <Tooltip key={alert.id} content={`${alert.action_message} (Acknowledged: ${formatRelativeDate(acknowledgedAt(alert))})`}>
                                <span
                                        data-testid="acknowledged-alert"
                                        data-alert-id={alert.id}
                                        data-alert-title={alert.action_title}
                                        className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 whitespace-nowrap ${alert.id === arrivedAlertId ? 'animate-ack-glow motion-safe:animate-ack-arrive' : ''}`}
                                >
                                        <FontAwesomeIcon icon={faCheck} />
                                        {alert.action_title}
                                </span>
                        </Tooltip>
                ));

        // A device younger than a day has nothing to chart yet. Named because both
        // the heading row's range selector and the charts below it hang off it.
        const isWarmingUp = device?.created_at
                ? (Date.now() - new Date(device.created_at).getTime()) < 24 * 60 * 60 * 1000
                : false;

        const goBack = useCallback(() => navigate(-1), [navigate]);

        const handleRenameDevice = useCallback(async (devId, roomId, newName) => {
                try {
                        await updateDevice(devId, { name: newName, room_id: parseInt(roomId) });
                        renameModal.close();
                        refreshDevice();
                        showToast('success', 'Device renamed successfully');
                } catch (err) {
                        showToast('error', 'Failed to rename device');
                }
        }, [renameModal, refreshDevice, showToast]);

        const handleMoveDevice = useCallback(async (devId, roomId, devName) => {
                try {
                        await updateDevice(devId, { name: devName, room_id: parseInt(roomId) });
                        moveModal.close();
                        refreshDevice();
                        showToast('success', 'Device moved successfully');
                } catch (err) {
                        showToast('error', 'Failed to move device');
                }
        }, [moveModal, refreshDevice, showToast]);

        // Uploads the image and reports the Pl@ntNet identification outcome.
        // Returns { ok, notify }: ok lets the modal close itself, and notify() is
        // deferred so the modal can show the toast only after its overlay closes.
        const handleSaveImage = useCallback(async (imageBlob) => {
                const formData = new FormData();
                formData.append("file", imageBlob);
                try {
                        const res = await uploadDeviceImage(deviceId, formData);
                        if (!res.ok) throw new Error();

                        let identification = null;
                        try {
                                const body = await res.json();
                                identification = body?.identification ?? null;
                        } catch (_) {
                                // Older responses may not include identification; ignore.
                        }

                        setImageVersion(v => v + 1);
                        refreshDevice();

                        return {
                                ok: true,
                                notify: () => {
                                        if (identification?.status === 'identified') {
                                                showToast('success', `Plant identified as ${identification.family}`);
                                        } else if (identification?.status === 'not_identified' || identification?.status === 'failed') {
                                                showToast('warning', 'Image saved, but plant identification unsuccessful');
                                        } else {
                                                showToast('success', 'Image uploaded successfully');
                                        }
                                },
                        };
                } catch (err) {
                        return { ok: false, notify: () => showToast('error', 'Failed to upload image') };
                }
        }, [deviceId, refreshDevice, showToast]);

        const handleDeleteImage = useCallback(async () => {
                if (!device?.image) return;
                try {
                        const res = await deleteDeviceImage(device.image.id);
                        if (!res.ok) throw new Error();
                        deleteImageModal.close();
                        refreshDevice();
                        showToast('success', 'Image deleted successfully');
                } catch (err) {
                        showToast('error', 'Failed to delete image');
                }
        }, [device, deleteImageModal, refreshDevice, showToast]);

        const handleDeleteDevice = useCallback(async () => {
                try {
                        await deleteDevice(deviceId);
                        deleteDeviceModal.close();
                        showToast('success', 'Device deleted successfully');
                        navigate('/');
                } catch (err) {
                        showToast('error', 'Failed to delete device');
                }
        }, [deviceId, deleteDeviceModal, navigate, showToast]);

        if (loading) return <div className="page-gradient min-h-screen flex items-center justify-center">Loading...</div>;
        if (error || !device || !sensorData || !deviceInfo) {
                return <div className="page-gradient min-h-screen flex items-center justify-center text-status-critical">{error || 'Failed to load device'}</div>;
        }

        return (
                <div className="bg-gradient-to-br from-bg-page to-blue-50 min-h-screen">
                        <Header customer={customer} />
                        <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
                                <div className="mb-8">
                                        <div className="bg-white rounded-[20px] shadow-[0_10px_30px_rgba(0,0,0,0.08)] p-8 mb-8">
                                                <div className="flex items-center justify-between gap-6">
                                                        <div className="flex items-center gap-6">
                                                                <button onClick={goBack} className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-colors">
                                                                        <FontAwesomeIcon icon={faArrowLeft} className="text-text-weak" />
                                                                </button>
                                                                <div className="flex items-center gap-3">
                                                                        <FontAwesomeIcon icon={faLocationDot} className="text-brand text-xl" />
                                                                        <h2 data-testid="device-name" className="text-3xl font-bold text-text-strong">{device.name}</h2>
                                                                </div>
                                                        </div>
                                                        <div className="flex items-center gap-6">
                                                                <button onClick={() => renameModal.open(device)} data-testid="device-rename-open" className="text-brand hover:text-brand-2" title="Rename device"><FontAwesomeIcon icon={faPencilAlt} /></button>
                                                                <button onClick={() => moveModal.open(device)} data-testid="device-move-open" className="text-brand hover:text-brand-2" title="Move device"><FontAwesomeIcon icon={faArrowsAltH} /></button>
                                                                <button onClick={() => deleteDeviceModal.open()} data-testid="device-delete-open" className="text-red-500" title="Delete device"><FontAwesomeIcon icon={faTrash} /></button>
                                                        </div>
                                                </div>
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-8">
                                                <div className="lg:col-span-3 bg-white rounded-[20px] shadow-[0_10px_30px_rgba(0,0,0,0.08)] p-8">
                                                        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                                                                <div className="relative md:col-span-3">
                                                                        <img data-testid="device-image" className="w-full h-auto rounded-[14px] object-cover" src={deviceInfo.imgSrc} alt="plant" />
                                                                        <div className="absolute top-2 right-2 flex gap-2">
                                                                                <button onClick={() => uploadModal.open()} data-testid="device-image-upload-open" className="w-8 h-8 bg-white/80 rounded-full flex items-center justify-center"><FontAwesomeIcon icon={faPencilAlt} /></button>
                                                                                {device.image && <button onClick={() => deleteImageModal.open()} data-testid="device-image-delete-open" className="w-8 h-8 bg-white/80 rounded-full flex items-center justify-center"><FontAwesomeIcon icon={faTrash} className="text-red-500" /></button>}
                                                                        </div>
                                                                </div>
                                                                <div className="md:col-span-2">
                                                                        <h3 className="text-xl font-bold text-text-strong mb-6">Plant Information</h3>
                                                                        <div className="space-y-4">
                                                                                <div>
                                                                                        <div className="text-sm font-semibold text-text-weak">Family</div>
                                                                                        <div className="flex items-center gap-1.5">
                                                                                                {device.plant_profile?.id && user?.roleId === ROLE_SUPERADMIN ? (
                                                                                                        <Link to={`/plants/${device.plant_profile.id}`} data-testid="device-family-link" className="text-brand font-medium hover:underline">
                                                                                                                {device.plant_profile.family}
                                                                                                        </Link>
                                                                                                ) : (
                                                                                                        <span data-testid="device-family-text" className="text-text-strong">{device.plant_profile?.family || 'General'}</span>
                                                                                                )}
                                                                                                {device.plant_profile?.description && (
                                                                                                        <Tooltip content={device.plant_profile.description}>
                                                                                                                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-900 text-white text-[10px] font-bold leading-none cursor-help">?</span>
                                                                                                        </Tooltip>
                                                                                                )}
                                                                                        </div>
                                                                                </div>
                                                                                <div><div className="text-sm font-semibold text-text-weak">Site</div><div data-testid="device-location" className="text-text-strong">{siteName} / {roomName}</div></div>
                                                                                <div><div className="text-sm font-semibold text-text-weak">Installed</div><div className="text-text-strong">{deviceInfo.installedDate}</div></div>
                                                                                <div><div className="text-sm font-semibold text-text-weak">Device-ID</div><div data-testid="device-id" className="text-text-strong font-mono">{device.id}</div></div>
                                                                                <div><div className="text-sm font-semibold text-text-weak">Firmware version</div><div data-testid="device-firmware" className="text-text-strong font-mono">{device.firmware_version}</div></div>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </div>

                                                <div className="lg:col-span-2 relative bg-white rounded-[20px] shadow-[0_10px_30px_rgba(0,0,0,0.08)] p-8">
                                                        <h3 className="text-xl font-bold text-text-strong mb-6">Alerts & Actions</h3>
                                                        {openAlerts.length > 0 ? (
                                                                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 pb-20">
                                                                        {openAlerts.map((alert) => {
                                                                                const sIcon = getSensorIcon(alert.sensor_type);
                                                                                return (
                                                                                        <div key={alert.id} data-testid="device-alert" data-alert-title={alert.action_title} className="p-3 bg-red-50 border-l-4 border-red-500 rounded-lg">
                                                                                                <div className="flex items-center justify-between mb-1">
                                                                                                        <div className="flex items-center gap-2">
                                                                                                                <FontAwesomeIcon icon={sIcon.icon} className={sIcon.color} />
                                                                                                                <span className="font-semibold">{alert.action_title}</span>
                                                                                                        </div>
                                                                                                        <button
                                                                                                                type="button"
                                                                                                                onClick={() => handleAcknowledge(alert)}
                                                                                                                data-testid="device-alert-acknowledge"
                                                                                                                className="text-sm font-medium text-brand hover:underline"
                                                                                                        >
                                                                                                                Acknowledge
                                                                                                        </button>
                                                                                                </div>
                                                                                                <p className="text-sm text-gray-700">{alert.action_message}</p>
                                                                                        </div>
                                                                                );
                                                                        })}
                                                                </div>
                                                        ) : acknowledgedAlerts.length > 0 ? (
                                                                <div data-testid="device-alerts-acknowledged" className="flex flex-col items-center justify-center min-h-[200px] text-center">
                                                                        <FontAwesomeIcon icon={faCheck} className="text-3xl text-yellow-500 mb-3" />
                                                                        <p className="text-lg font-semibold text-text-strong">All alerts acknowledged</p>
                                                                        <p className="text-sm text-text-weak mt-1">You can find them under Trends & Analytics.</p>
                                                                </div>
                                                        ) : (
                                                                <div className="flex flex-col items-center justify-center min-h-[200px]">
                                                                        <Lottie animationData={happyPlantAnimation} loop style={{ width: 180, height: 180 }} />
                                                                        <p data-testid="device-thriving" className="text-xl font-semibold text-green-600 mt-4">I am thriving!</p>
                                                                </div>
                                                        )}
                                                        <div className="absolute bottom-8 right-8 flex items-center space-x-4">
                                                                {device.water_level_sensor && <div className="flex items-center space-x-2"><FontAwesomeIcon icon={faWater} /><span>{sensorData.water_level}%</span></div>}
                                                                {deviceInfo.batteryInfo && <div className="flex items-center space-x-2"><FontAwesomeIcon icon={deviceInfo.batteryInfo.icon} /><span>{deviceInfo.battery}%</span></div>}
                                                        </div>
                                                </div>
                                        </div>

                                        <div className="bg-white rounded-[20px] shadow-[0_10px_30px_rgba(0,0,0,0.08)] p-8">
                                                <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                                                        <h3 className="text-xl font-bold text-text-strong">Trends & Analytics</h3>
                                                        {!isWarmingUp && (
                                                                <HistoryRangeSelector
                                                                        value={trendsRange}
                                                                        onChange={setTrendsRange}
                                                                        firstReadingAt={firstReadingAt}
                                                                        idPrefix="trends-range"
                                                                />
                                                        )}
                                                </div>
                                                {isWarmingUp ? (
                                                        <div className="flex flex-col items-center justify-center min-h-[250px]">
                                                                <p data-testid="device-warming-up" className="text-xl font-semibold text-brand">Getting to know your plant</p>
                                                                <p className="text-sm text-text-weak mt-2">Trends will appear once we have collected enough data.</p>
                                                        </div>
                                                ) : (
                                                        <>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                                        <div>
                                                                                <div className="flex items-center justify-between gap-3 mb-4 min-h-[28px]">
                                                                                        <h4 className="text-lg font-semibold" data-testid="chart-title" data-chart="temp">Temperature</h4>
                                                                                        <div className="flex flex-wrap items-center justify-end gap-2">{acknowledgedChips('temp')}</div>
                                                                                </div>
                                                                                <div data-testid="chart" data-chart="temp"><ReactECharts option={chartOptions.temp} style={{ height: '250px' }} /></div>
                                                                        </div>
                                                                        <div>
                                                                                <div className="flex items-center justify-between gap-3 mb-4 min-h-[28px]">
                                                                                        <h4 className="text-lg font-semibold" data-testid="chart-title" data-chart="moisture">Moisture</h4>
                                                                                        <div className="flex flex-wrap items-center justify-end gap-2">
                                                                                                {acknowledgedChips('moist')}
                                                                                                {device?.overwater_notice > 0 && (
                                                                                                        <Tooltip content={overwaterNoticeText(device.overwater_notice)}>
                                                                                                                <span data-testid="overwater-notice" className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 whitespace-nowrap">
                                                                                                                        <FontAwesomeIcon icon={faDroplet} />
                                                                                                                        {overwaterNoticeLabel}
                                                                                                                </span>
                                                                                                        </Tooltip>
                                                                                                )}
                                                                                                {moistureForecast?.warn && (
                                                                                                        <Tooltip content={`Fitted from the ${moistureForecast.leg_days} days since this plant was last watered. It is drying about ${Math.abs(moistureForecast.slope_pct_per_day).toFixed(1)}% a day.`}>
                                                                                                                <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 whitespace-nowrap">
                                                                                                                        <FontAwesomeIcon icon={faDroplet} />
                                                                                                                        {dryingSoonLabel(moistureForecast.days_until_dry)}
                                                                                                                </span>
                                                                                                        </Tooltip>
                                                                                                )}
                                                                                        </div>
                                                                                </div>
                                                                                <div data-testid="chart" data-chart="moisture"><ReactECharts option={chartOptions.moisture} style={{ height: '250px' }} /></div>
                                                                        </div>
                                                                        <div>
                                                                                <div className="flex items-center justify-between gap-3 mb-4 min-h-[28px]">
                                                                                        <h4 className="text-lg font-semibold" data-testid="chart-title" data-chart="light">Light</h4>
                                                                                        <div className="flex flex-wrap items-center justify-end gap-2">{acknowledgedChips('light')}</div>
                                                                                </div>
                                                                                <div data-testid="chart" data-chart="light"><ReactECharts option={chartOptions.light} style={{ height: '250px' }} /></div>
                                                                        </div>
                                                                        <div>
                                                                                <div className="flex items-center justify-between gap-3 mb-4 min-h-[28px]">
                                                                                        <h4 className="text-lg font-semibold" data-testid="chart-title" data-chart="nutrition">Nutrition</h4>
                                                                                        <div className="flex flex-wrap items-center justify-end gap-2">{acknowledgedChips('nutrition')}</div>
                                                                                </div>
                                                                                <div data-testid="chart" data-chart="nutrition"><ReactECharts option={chartOptions.nutrition} style={{ height: '250px' }} /></div>
                                                                        </div>
                                                                </div>
                                                                <div className="mt-6 flex items-center gap-2 text-text-weak text-sm">
                                                                        <FontAwesomeIcon icon={faClock} />
                                                                        <span>Last update: {deviceInfo.dateTime}</span>
                                                                </div>
                                                        </>
                                                )}
                                        </div>
                                </div>
                        </main>
                        <Footer />

                        <MoveDeviceModal isOpen={moveModal.isOpen} onClose={moveModal.close} device={moveModal.item} sites={customer?.sites || []} onMove={handleMoveDevice} />
                        <RenameDeviceModal isOpen={renameModal.isOpen} onClose={renameModal.close} device={renameModal.item} onRename={handleRenameDevice} />
                        <ImageUploadModal isOpen={uploadModal.isOpen} onClose={uploadModal.close} onSave={handleSaveImage} />
                        <ConfirmationModal isOpen={deleteImageModal.isOpen} onClose={deleteImageModal.close} onConfirm={handleDeleteImage} title="Delete Image" message="Are you sure you want to delete this image?" />
                        <ConfirmationModal isOpen={deleteDeviceModal.isOpen} onClose={deleteDeviceModal.close} onConfirm={handleDeleteDevice} title="Delete Device" message="Are you sure you want to delete the device?" />
                </div>
        );
};

export default DeviceDetailsPage;
